import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Captions, Loader2, Maximize, Minimize, Pause, PictureInPicture2, Play, RotateCcw, RotateCw, Settings, SkipForward, Volume2, VolumeX,
} from "lucide-react";
import { attachSource } from "@/features/player/sourceLoader";
import { useSubtitleTracks } from "@/features/player/useSubtitleTracks";
import { SeekBar } from "@/features/player/SeekBar";
import { SettingsMenu, SubtitleMenu } from "@/features/player/PlayerMenus";
import { EndedOverlay, NextEpisodeOverlay } from "@/features/player/PlayerOverlays";
import { fmtTime } from "@/lib/format";

const SAVE_EVERY_MS = 10000;
const IconBtn = ({ testId, label, onClick, children, className = "" }) => (
  <button data-testid={testId} aria-label={label} title={label} onClick={onClick} className={`grid h-9 w-9 sm:h-11 sm:w-11 place-items-center rounded-full text-white/90 hover:text-[#FFD86A] hover:bg-[#D4AF37]/10 active:scale-95 transition-[color,background-color,transform] ${className}`}>
    {children}
  </button>
);

export const VideoPlayer = ({ src, poster, subtitles, heading, subheading, startAt = 0, introStart = 0, introEnd = 0, nextEpisode, onNext, onProgress, onBack }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const controllerRef = useRef(null);
  const hideTimer = useRef(null);
  const latest = useRef({ t: 0, d: 0 });
  const [state, setState] = useState({ playing: false, current: 0, duration: 0, buffered: 0, waiting: true });
  const [volume, setVolume] = useState(() => Number(localStorage.getItem("aetryx_volume") ?? 1));
  const [muted, setMuted] = useState(false);
  const [visible, setVisible] = useState(true);
  const [menu, setMenu] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [quality, setQuality] = useState("Auto");
  const [subLang, setSubLang] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [ended, setEnded] = useState(false);
  const [autoNextCancelled, setAutoNextCancelled] = useState(false);
  const tracks = useSubtitleTracks(subtitles);
  const [attempt, setAttempt] = useState(0);
  const resumeAt = useRef(0);
  const v = () => videoRef.current;

  useEffect(() => {
    let ctrl;
    let alive = true;
    attachSource(v(), src).then((c) => {
      if (!alive) return c.destroy();
      ctrl = c;
      controllerRef.current = c;
    }).catch(() => alive && setState((s) => ({ ...s, waiting: false, error: true })));
    return () => { alive = false; ctrl?.destroy(); };
  }, [src, attempt]);

  useEffect(() => {
    if (!state.waiting || state.error || ended) return undefined;
    const t = setTimeout(() => setState((s) => (s.waiting ? { ...s, waiting: false, error: "stall" } : s)), 25000);
    return () => clearTimeout(t);
  }, [state.waiting, state.error, ended]);

  const retry = () => {
    resumeAt.current = latest.current.t || startAt;
    setState((s) => ({ ...s, error: false, waiting: true }));
    setAttempt((a) => a + 1);
  };

  const save = useCallback((opts) => {
    const { t, d } = latest.current;
    if (d > 0) onProgress?.(t, d, opts);
  }, [onProgress]);

  useEffect(() => {
    const id = setInterval(() => { if (!v()?.paused) save(); }, SAVE_EVERY_MS);
    const onHide = () => save({ keepalive: true });
    window.addEventListener("pagehide", onHide);
    return () => { clearInterval(id); window.removeEventListener("pagehide", onHide); save({ keepalive: true }); };
  }, [save]);

  useEffect(() => { if (v()) { v().volume = volume; v().muted = muted; } localStorage.setItem("aetryx_volume", String(volume)); }, [volume, muted]);
  useEffect(() => { if (v()) v().playbackRate = speed; }, [speed]);
  useEffect(() => {
    const list = v()?.textTracks;
    if (!list) return;
    for (let i = 0; i < list.length; i++) list[i].mode = list[i].language === subLang ? "showing" : "disabled";
  }, [subLang, tracks]);

  const togglePlay = useCallback(() => { const el = v(); if (!el) return; el.paused ? el.play().catch(() => {}) : el.pause(); }, []);
  const seekTo = useCallback((t) => { const el = v(); if (el) { el.currentTime = Math.max(0, Math.min(t, el.duration || t)); setEnded(false); } }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else if (wrapRef.current?.requestFullscreen) wrapRef.current.requestFullscreen();
    else v()?.webkitEnterFullscreen?.();
  }, []);
  const togglePip = async () => {
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await v().requestPictureInPicture();
    } catch { /* unsupported */ }
  };
  const pipSupported = typeof document !== "undefined" && document.pictureInPictureEnabled;

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const poke = useCallback(() => {
    setVisible(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { if (!v()?.paused) { setVisible(false); setMenu(null); } }, 3000);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      const el = v();
      const k = e.key.toLowerCase();
      if (k === " " || k === "k") { e.preventDefault(); togglePlay(); }
      else if (k === "arrowright") { e.preventDefault(); seekTo(el.currentTime + 10); }
      else if (k === "arrowleft") { e.preventDefault(); seekTo(el.currentTime - 10); }
      else if (k === "arrowup") { e.preventDefault(); setVolume((x) => Math.min(1, +(x + 0.1).toFixed(2))); setMuted(false); }
      else if (k === "arrowdown") { e.preventDefault(); setVolume((x) => Math.max(0, +(x - 0.1).toFixed(2))); }
      else if (k === "f") toggleFullscreen();
      else if (k === "m") setMuted((m) => !m);
      else return;
      poke();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePlay, seekTo, toggleFullscreen, poke]);

  const ev = {
    onLoadedMetadata: (e) => {
      const el = e.currentTarget;
      const at = resumeAt.current || startAt;
      if (at > 0 && at < el.duration - 5) el.currentTime = at;
      el.play().catch(() => setState((s) => ({ ...s, playing: false })));
    },
    onTimeUpdate: (e) => {
      const el = e.currentTarget;
      latest.current = { t: el.currentTime, d: el.duration || 0 };
      const b = el.buffered.length ? el.buffered.end(el.buffered.length - 1) : 0;
      setState((s) => ({ ...s, current: el.currentTime, duration: el.duration || 0, buffered: b }));
    },
    onProgress: (e) => { const el = e.currentTarget; if (el.buffered.length) setState((s) => ({ ...s, buffered: el.buffered.end(el.buffered.length - 1) })); },
    onPlay: () => { setState((s) => ({ ...s, playing: true })); setEnded(false); poke(); },
    onPause: () => { setState((s) => ({ ...s, playing: false })); setVisible(true); save(); },
    onWaiting: () => setState((s) => ({ ...s, waiting: true })),
    onPlaying: () => setState((s) => ({ ...s, waiting: false })),
    onCanPlay: () => setState((s) => ({ ...s, waiting: false })),
    onEnded: () => { latest.current.t = latest.current.d; save(); setEnded(true); setVisible(true); },
    onError: () => setState((s) => ({ ...s, waiting: false, error: true })),
  };

  const { playing, current, duration, buffered, waiting } = state;
  const showSkipIntro = introEnd > introStart && current >= introStart && current < introEnd && !ended;
  const VolIcon = muted || volume === 0 ? VolumeX : Volume2;

  return (
    <div
      ref={wrapRef}
      data-testid="video-player"
      onMouseMove={poke}
      onTouchStart={poke}
      className={`relative h-[100dvh] w-full bg-black overflow-hidden select-none ${visible ? "" : "cursor-none"}`}
    >
      <video
        ref={videoRef}
        data-testid="video-element"
        poster={poster}
        playsInline
        preload="metadata"
        className="absolute inset-0 h-full w-full object-contain"
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        {...ev}
      >
        {tracks.map((t) => <track key={t.lang} kind="subtitles" srcLang={t.lang} label={t.label} src={t.src} />)}
      </video>

      {state.error && (
        <div data-testid="player-error" className="absolute inset-0 z-20 grid place-items-center bg-black/80 text-center px-6">
          <div><p className="eyebrow">{state.error === "stall" ? "Connection problem" : "Playback error"}</p>
            <p className="mt-2 text-white/70">{state.error === "stall" ? "The stream stopped loading. Check your connection and try again." : "This video can't be played right now."}</p>
            <div className="mt-5 flex justify-center gap-3">
              <button data-testid="player-retry-btn" onClick={retry} className="btn-gold px-6 py-2.5 text-sm">Retry</button>
              {onBack && <button data-testid="player-error-back" onClick={onBack} className="btn-ghost-gold px-6 py-2.5 text-sm">Go back</button>}
            </div>
          </div>
        </div>
      )}
      {waiting && !ended && !state.error && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center"><Loader2 data-testid="player-buffering" className="h-14 w-14 animate-spin text-[#FFD86A]" /></div>
      )}
      {!playing && !waiting && !ended && (
        <button data-testid="player-center-play-btn" onClick={togglePlay} className="absolute inset-0 m-auto grid h-20 w-20 place-items-center rounded-full border border-[#FFD86A]/70 bg-black/50 text-[#FFD86A] backdrop-blur hover:scale-110 transition-transform" aria-label="Play">
          <Play className="h-9 w-9 fill-current ml-1" />
        </button>
      )}

      <div className={`absolute inset-x-0 top-0 z-20 flex items-center gap-4 bg-gradient-to-b from-black/85 to-transparent px-4 sm:px-8 pt-5 pb-16 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <IconBtn testId="player-back-btn" label="Back" onClick={onBack}><ArrowLeft className="h-6 w-6" /></IconBtn>
        <div className="min-w-0">
          <p data-testid="player-heading" className="font-display text-base sm:text-xl font-bold gold-text truncate">{heading}</p>
          {subheading && <p data-testid="player-subheading" className="text-xs sm:text-sm text-white/70 truncate">{subheading}</p>}
        </div>
      </div>

      {showSkipIntro && (
        <button data-testid="skip-intro-btn" onClick={() => seekTo(introEnd)} className="absolute bottom-32 right-4 sm:right-10 z-30 btn-ghost-gold px-6 py-3 text-sm bg-black/70 fade-up">
          Skip Intro <SkipForward className="h-4 w-4" />
        </button>
      )}

      {ended && nextEpisode && !autoNextCancelled && <NextEpisodeOverlay next={nextEpisode} onPlay={onNext} onCancel={() => setAutoNextCancelled(true)} />}
      {ended && (!nextEpisode || autoNextCancelled) && <EndedOverlay onReplay={() => { seekTo(0); v().play(); }} onBack={onBack} />}

      <div data-testid="player-controls" className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/95 via-black/60 to-transparent px-3 sm:px-8 pb-4 sm:pb-6 pt-20 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <SeekBar current={current} duration={duration} buffered={buffered} onSeek={seekTo} />
        <div className="mt-2 flex items-center gap-0.5 sm:gap-1">
          <IconBtn testId="player-play-pause-btn" label={playing ? "Pause" : "Play"} onClick={togglePlay}>
            {playing ? <Pause className="h-6 w-6 fill-current" /> : <Play className="h-6 w-6 fill-current" />}
          </IconBtn>
          <div className="hidden sm:flex items-center gap-1">
            <IconBtn testId="player-rewind-btn" label="Back 10 seconds" onClick={() => seekTo(current - 10)}><RotateCcw className="h-5 w-5" /></IconBtn>
            <IconBtn testId="player-forward-btn" label="Forward 10 seconds" onClick={() => seekTo(current + 10)}><RotateCw className="h-5 w-5" /></IconBtn>
          </div>
          <div className="group/vol flex items-center">
            <IconBtn testId="player-mute-btn" label={muted ? "Unmute" : "Mute"} onClick={() => setMuted((m) => !m)}><VolIcon className="h-5 w-5" /></IconBtn>
            <input
              data-testid="player-volume-slider"
              type="range" min="0" max="1" step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(Number(e.target.value)); setMuted(false); }}
              className="gold-range hidden sm:block w-0 opacity-0 group-hover/vol:w-24 group-hover/vol:opacity-100 focus:w-24 focus:opacity-100 transition-[width,opacity] duration-300"
              aria-label="Volume"
            />
          </div>
          <span data-testid="player-time" className="ml-1 sm:ml-2 text-xs sm:text-sm tabular-nums text-white/85 whitespace-nowrap">{fmtTime(current)} <span className="text-white/40">/</span> {fmtTime(duration)}</span>
          <div className="ml-auto flex items-center gap-0.5 sm:gap-1 relative">
            {nextEpisode && (
              <button data-testid="player-next-episode-btn" onClick={onNext} className="hidden sm:flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-white/90 hover:text-[#FFD86A] hover:bg-white/10 transition-colors">
                <SkipForward className="h-5 w-5" /> Next Episode
              </button>
            )}
            {nextEpisode && <IconBtn testId="player-next-episode-icon-btn" label="Next episode" onClick={onNext} className="sm:hidden"><SkipForward className="h-5 w-5" /></IconBtn>}
            <IconBtn testId="player-subtitles-btn" label="Subtitles" onClick={() => setMenu(menu === "subs" ? null : "subs")} className={subLang ? "text-[#FFD86A]" : ""}><Captions className="h-5 w-5" /></IconBtn>
            <IconBtn testId="player-settings-btn" label="Settings" onClick={() => setMenu(menu === "settings" ? null : "settings")}><Settings className="h-5 w-5" /></IconBtn>
            {pipSupported && <IconBtn testId="player-pip-btn" label="Picture in picture" onClick={togglePip}><PictureInPicture2 className="h-5 w-5" /></IconBtn>}
            <IconBtn testId="player-fullscreen-btn" label="Fullscreen" onClick={toggleFullscreen}>{fullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}</IconBtn>
            {menu === "subs" && <SubtitleMenu tracks={tracks} active={subLang} onSelect={(l) => { setSubLang(l); setMenu(null); }} />}
            {menu === "settings" && (
              <SettingsMenu speed={speed} onSpeed={(s) => { setSpeed(s); setMenu(null); }} quality={quality} onQuality={(q) => { setQuality(q); controllerRef.current?.setQuality(q); setMenu(null); }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
